import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './css/theme.css';
import './css/App.css';
import logo from './images/logo.svg';
import TimeTable from './TimeTable';
import About from './About';
import PersonalInfo from './PersonalInfo';
import Statistics from './Statistics';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import Login from './common/Login';
import Register from './common/Register';
import { AuthProvider } from './common/AuthContext';
import ProtectedRoute from './common/ProtectedRoute';
import { useAuth } from './common/AuthContext';
import Logout from './common/Logout';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { isAdmin } from './common/utils';
import Admin from './Admin';
import { TimeTableProvider, useTimeTable } from './contexts/TimeTableContext';
import { TimerProvider } from './contexts/TimerContext';
import { GoalProvider } from './contexts/GoalContext';
import { StreakProvider } from './contexts/StreakContext';
import Daily from './Daily';
import Timer from './Timer';
import Sidebar from './common/Sidebar';
import SubjectManagement from './SubjectManagement';
import ObserverManagement from './ObserverManagement';
import OAuthCallback from './common/OAuthCallback';
import OAuthRegister from './common/OAuthRegister';
import PrivacyPolicy from './PrivacyPolicy';
import AiUsagePolicy from './AiUsagePolicy';
import Announcements from './Announcements';
import AnnouncementPopup from './components/AnnouncementPopup';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import RecurringSchedule from './RecurringSchedule';
import { RecurringProvider } from './contexts/RecurringContext';
import StudyRoom from './StudyRoom';
import Board from './Board';
import ActiveRoomIcon from './components/ActiveRoomIcon';
import { StudyRoomProvider } from './contexts/StudyRoomContext';
import Vocabulary from './Vocabulary';
import VocabularyQuiz from './VocabularyQuiz';
import VocabularyQuizPlay from './VocabularyQuizPlay';
import VocabularyQuizResult from './VocabularyQuizResult';
import VocabularyQuizPaper from './VocabularyQuizPaper';
import VocabularyQuizSheet from './VocabularyQuizSheet';
import VocabularyReview from './VocabularyReview';
import VocabularyWordbook from './VocabularyWordbook';
import { VocabularyProvider } from './contexts/VocabularyContext';
import { usePageTracking } from './hooks/usePageTracking';

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [apiResponse, setApiResponse] = React.useState('');
  const { user, isAuthenticated } = useAuth();
  const [lastPath, setLastPath] = useState('');
  const { initializeData, finalizeData } = useTimeTable();
  const [isMenuVisible, setIsMenuVisible] = useState(true);
  const { resolvedTheme } = useTheme();
  usePageTracking();

  useEffect(() => {
    if (location.pathname === '/timetable' && user) {
      initializeData();
    }
    else if (lastPath === '/timetable') {
      finalizeData();
    }
    setLastPath(location.pathname);
  }, [location, user]);

  return (
    <div className="app-container">
      <Sidebar isMenuVisible={isMenuVisible} setIsMenuVisible={setIsMenuVisible} />
      <ActiveRoomIcon />
      <AnnouncementPopup />

      {/* 오른쪽 컨텐츠 영역 */}
      <main className="App">
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme={resolvedTheme}
        />
        <Routes>
          <Route path="/" element={
            <header className="App-header">
              <img src={logo} className="App-logo" alt="logo" />
              <div>
                <p>{t('home.title')}&nbsp;{t('home.version')}</p>
                <p>{apiResponse}</p>
              </div>
              <div>
                <button onClick={async () => {
                  try {
                    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/test`);
                    const data = await response.json();
                    setApiResponse(data.timestamp);
                  } catch (error) {
                    console.error('API 호출 에러:', error);
                    toast.error(t('serverConnectionFailed') || '서버 연결에 실패했습니다.');
                  }
                }}>
                  {t('home.testApi')}
                </button>
              </div>
            </header>
          } />
          <Route path="/timetable" element={
            <ProtectedRoute>
              <TimeTable />
            </ProtectedRoute>
          } />
          <Route path="/studyroom" element={
            <ProtectedRoute>
              <StudyRoom />
            </ProtectedRoute>
          } />
          <Route path="/daily" element={
            <ProtectedRoute>
              <Daily />
            </ProtectedRoute>
          } />
          <Route path="/statistics" element={
            <ProtectedRoute>
              <Statistics />
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute>
              <Admin />
            </ProtectedRoute>
          } />
          <Route path="/logout" element={
            <ProtectedRoute>
              <Logout />
            </ProtectedRoute>
          } />
          <Route path="/subjects" element={
            <ProtectedRoute>
              <SubjectManagement />
            </ProtectedRoute>
          } />
          <Route path="/observer" element={
            <ProtectedRoute>
              <ObserverManagement />
            </ProtectedRoute>
          } />
          <Route path="/recurring" element={
            <ProtectedRoute>
              <RecurringSchedule />
            </ProtectedRoute>
          } />
          <Route path="/timer" element={
            <ProtectedRoute>
              <Timer />
            </ProtectedRoute>
          } />
          <Route path="/personalinfo" element={
            <ProtectedRoute>
              <PersonalInfo />
            </ProtectedRoute>
          } />
          <Route path="/vocabulary" element={
            <ProtectedRoute>
              <Vocabulary />
            </ProtectedRoute>
          } />
          <Route path="/vocabulary/quiz" element={
            <ProtectedRoute>
              <VocabularyQuiz />
            </ProtectedRoute>
          } />
          <Route path="/vocabulary/quiz/:quizId" element={
            <ProtectedRoute>
              <VocabularyQuizPlay />
            </ProtectedRoute>
          } />
          <Route path="/vocabulary/quiz/:quizId/result" element={
            <ProtectedRoute>
              <VocabularyQuizResult />
            </ProtectedRoute>
          } />
          <Route path="/vocabulary/quiz/:quizId/paper" element={
            <ProtectedRoute>
              <VocabularyQuizPaper />
            </ProtectedRoute>
          } />
          <Route path="/vocabulary/quiz/:quizId/sheet" element={
            <ProtectedRoute>
              <VocabularyQuizSheet />
            </ProtectedRoute>
          } />
          <Route path="/vocabulary/wordbook" element={
            <ProtectedRoute>
              <VocabularyWordbook />
            </ProtectedRoute>
          } />
          <Route path="/vocabulary/wordbook/:wordbookId" element={
            <ProtectedRoute>
              <VocabularyWordbook />
            </ProtectedRoute>
          } />
          <Route path="/vocabulary/review" element={
            <ProtectedRoute>
              <VocabularyReview />
            </ProtectedRoute>
          } />
          <Route path="/board" element={
            <ProtectedRoute>
              <Board />
            </ProtectedRoute>
          } />
          <Route path="/announcements" element={<Announcements />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/ai-usage" element={<AiUsagePolicy />} />
          <Route path="/oauth/callback" element={<OAuthCallback />} />
          <Route path="/oauth/register" element={<OAuthRegister />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="*" element={<div className="App-header">{t('error.pageNotFound')}</div>} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <TimeTableProvider>
        <TimerProvider>
          <GoalProvider>
            <StreakProvider>
              <RecurringProvider>
                <StudyRoomProvider>
                  <VocabularyProvider>
                    <AppContent />
                  </VocabularyProvider>
                </StudyRoomProvider>
              </RecurringProvider>
            </StreakProvider>
          </GoalProvider>
        </TimerProvider>
      </TimeTableProvider>
    </ThemeProvider>
  );
}

export default App;
